import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'

import { NotificationBanner } from '@weberlo/components'

import { ApiError, Billing, BillingService } from 'generated'
import useConfig from 'hooks/useConfig'
import useToast from 'hooks/useToast'
import { getCurrentDate } from 'utils'

import { Response } from 'types'

const PaymentBanner = () => {
  const navigate = useNavigate()

  const { toast } = useToast()
  const { workspace, drawerOpen, timezone } = useConfig()
  const [message, setMessage] = useState('')

  const { isLoading, data, error } = useQuery<Response<Billing>, ApiError>({
    queryKey: ['billing', workspace],
    queryFn: () => BillingService.getBilling(workspace) as Response<Billing>,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (error) {
      toast({
        message: error?.message || 'Fetching Error',
        alertColor: 'error',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (isLoading) return

    const { current_events, max_events, plan, expire_date } =
      data?.data?.subscription || {}

    // Redirect to manage subscription page if subscription is expired
    if (expire_date) {
      const expireDate = new Date(expire_date)
      const gracePeriodDays = 2
      expireDate.setDate(expireDate.getDate() + gracePeriodDays) // Extend expiration date by the grace period days
      const currentDate = new Date(getCurrentDate(timezone))

      if (expireDate < currentDate) {
        navigate('/subscription', { replace: true })
      }
    }

    if (current_events && max_events && current_events >= max_events) {
      navigate('/settings/plan', { replace: true })
    }

    // Show notification banner if page views are running out
    const usedViews = ((current_events || 0) / (max_events || 0)) * 100

    if (usedViews >= 80 && usedViews < 100) {
      setMessage('Less than 20% page views left')
    } else if (usedViews >= 100) {
      setMessage('')
    } else if (plan === 'free') {
      setMessage('You are on a free plan')
    } else {
      setMessage('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, workspace, timezone])

  if (isLoading || !message) return null

  return (
    <NotificationBanner
      btnLabel="Upgrade now"
      style={{ maxWidth: drawerOpen ? '1440px' : '1660px' }}
      title={message}
      onClick={() => navigate('/settings/plan', { replace: true })}
    />
  )
}

export default PaymentBanner
